// src/components/FAQsSectionContact.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaPlus, 
  FaMinus, 
  FaEnvelope,
  FaWhatsapp,
  FaMapMarkerAlt 
} from 'react-icons/fa';

const FAQsSectionContact = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // FAQ data specific for contact page
  const faqs = [
    {
      question: "How can I make a booking?",
      answer: "You can make a booking directly through our website, call our reservations team at +92303 4935404 , or visit our reception at The Centaurus Mall. Online bookings are confirmed instantly."
    },
    {
      question: "What is your cancellation policy?",
      answer: "Free cancellation is available up to 48 hours before check-in. Cancellations within 48 hours may incur a one-night charge. For long-term stays (1 month+), a 7-day notice is required."
    },
    {
      question: "Do you offer airport pickup service?",
      answer: "Yes, we offer airport pickup and drop-off services at additional charges. Please inform us at least 24 hours in advance with your flight details to arrange transportation."
    },
    {
      question: "Can I visit the apartment before booking?",
      answer: "Absolutely! We encourage scheduled visits. Please contact our concierge team to arrange a tour of our apartments during business hours (9 AM - 8 PM)."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, Mastercard, American Express), local bank transfers, and cash payments at the reception. Online payments are securely processed."
    },
    {
      question: "Is there a minimum stay requirement?",
      answer: "We offer flexible stays with no minimum for standard bookings. For special rates and long-term stays, a minimum of 7 nights may apply during peak seasons."
    },
    {
      question: "How do I contact support after booking?",
      answer: "Our 24/7 concierge team is available via phone at +92303 4935404 , email at info@stayinhospitality.com, or through the WhatsApp button on our website."
    },
    {
      question: "Can I modify my booking dates?",
      answer: "Yes, date modifications are subject to availability. Please contact our reservations team at least 48 hours before your original check-in date to make changes."
    }
  ];

  // Quick contact options
  const quickContacts = [
    { icon: <FaMapMarkerAlt />, label: "Head Office", value: "Centaurus Complex, Jinnah Avenue, Islamabad", action: "tel:+92303 4935404 " },
    { icon: <FaEnvelope />, label: "Email", value: "info@stayin.com", action: "mailto:info@stayin.com" },
    { icon: <FaWhatsapp />, label: "WhatsApp", value: "+92 303 4935404", action: "https://wa.me/923034935404" },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    },
  };

  return (
    <section className="py-12 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">

      <div className="container-custom relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 space-y-2"
          >
            <div className="flex items-center justify-center gap-3">
              <div className="w-12 h-[2px] bg-[#c9a86c]"></div>
              <span className="text-[#c9a86c] font-medium tracking-[0.2em] text-xs uppercase">
                Got Questions?
              </span>
              <div className="w-12 h-[2px] bg-[#c9a86c]"></div>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-light text-[#1a2b3c] !mb-4">
              Frequently Asked{' '}
              <span className="font-bold text-[#c9a86c]">Questions</span>
            </h2>
            
            <p className="text-gray-500 md:text-[17px] 2xl:text-lg max-w-2xl mx-auto">
              Find quick answers to common questions about bookings, cancellations, and more
            </p>
          </motion.div>

          {/* FAQs Accordion */}
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="space-y-4"
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white p-1 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-100 overflow-hidden"
              >
                {/* Question Button */}
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none group"
                >
                  <div className="flex items-center gap-3">
                    <span className="2xl:text-lg text-[15px] text-[#1a2b3c] font-medium">
                      {faq.question}
                    </span>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#c9a86c]/10 flex items-center justify-center">
                    {openIndex === index ? (
                      <FaMinus className="text-[#c9a86c] text-sm" />
                    ) : (
                      <FaPlus className="text-[#c9a86c] text-sm" />
                    )}
                  </div>
                </button>

                {/* Answer (Conditional) */}
                {openIndex === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-5 pt-2 border-t border-gray-100"
                  >
                    <div className="flex items-start gap-3">
                      {/* <div className="w-8 h-8 flex-shrink-0"></div> Spacer for alignment */}
                      <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQsSectionContact;